// Builds a three.js scene by extruding the 2D model — reusing the same footprint
// functions the renderer draws with, so the 3D preview matches the plan.
//
// Fase 1 scope: sauna/steam rooms (walls + ceiling + benches), plus a simple
// extruded footprint for other products. Glass, openings, doors, node objects and
// the imported background come in later phases.

import * as THREE from 'three';
import type {
  BenchProfilePoint,
  Connection,
  Drawing,
  InnerWall,
  PartModel3d,
  ProductInstance,
  ProductTemplate,
  Vec2,
  Wall,
} from '../../shared/types';
import {
  profileRing,
  profileSweepFaces,
  sweepMesh,
} from '../benchProfile';
import {
  instanceCorners,
  saltWallHull,
} from '../render';
import {
  heaterWallBands,
  polyBenchCornerBands,
  polyInnerCarved,
  polyInnerWallFill,
  polyWall,
  resolvedOutline,
  withWallCuts,
} from '../polyRoom';
import {
  nodeBenchSolids,
  roomBenchSolids,
  structureColor,
} from '../benchSolids';
import { wallThicknessOf } from '../dent';
import {
  isTilted,
  mul as mulMat3,
  partMatrix,
  rotZ,
} from '../tilt';
import {
  wallDoorSwing,
  wallFeatureBase,
  wallFeatureLayers,
  wallFeatureQuad,
  wallFeatureSpans,
  wallFeatureTop,
  wallMeshFaces,
  wallPaneQuad,
  wallPockets,
} from '../wallFeatures';
import { inWallParts } from '../wallCuts';
import {
  partRisers,
  RISER_COLORS,
  riserRing,
} from '../risers';
import {
  externalHeight,
  heaterWallBase,
  heaterWallTop,
  innerWallBase,
  innerWallTop,
  innerWallTopAt,
  interiorHeight,
  objectHeight,
  partHeight,
  partSpan,
  polygonBase,
  polygonHeight,
  roomBase,
  wallBase,
  wallHeight,
} from '../heights';
import {
  boxRing,
  extrudePrism,
  facesMesh,
  hullMesh,
  to3,
} from './extrude';
import {
  tileTexture,
  woodTexture,
} from './textures';
import {
  HEATER_BODY_COLOR,
  HEATER_COVER_COLOR,
  instanceToWorld,
} from '../geometry';
import {
  isProjected,
  type PartAttachment,
  partLook,
} from '../partLook';
import { bodyModel } from '../builtModels';
import { unreachable } from '../../shared/exhaustive';
import {
  BUILT,
  type PickVolume,
  pieceSpanAt,
  type PieceStuff,
  roomFeaturePieces,
  thinPanel,
} from '../roomFeaturePieces';
import {
  ceilingColorOf,
  type CeilingField,
  hasCeiling,
  isFlatCeiling,
  resolveCeiling,
  underAt,
  wallTopAt,
} from '../ceiling';
import {
  prismUnder,
  shellParts,
  shellSeat,
} from '../ceilingMesh';
import { meshFromBuf } from './ceiling3d';
import {
  type Cladding,
  roomCladding,
} from './woods3d';
import { finishCladding } from './finishes3d';
import { takesFinish } from '../../shared/finishes';
import { takesWood } from '../../shared/woods';
import {
  PRODUCT_COLOR,
  solidGroups,
  solidMesh,
} from '../../shared/partSolids';
import { tubeMesh } from './pipes3d';
import { glazingMeshes } from './glazing3d';
import {
  connDiscipline,
  connLines3d,
  pipeTubes,
} from '../connections';
import {
  FLOOR_Z,
  lookOf,
  nodeOf,
  type NodeRole,
  ROLE,
  setNode,
  type Solid,
  withLook,
} from './sceneNodes';
import {
  backrestsOf,
  backrestSolids,
} from '../backrest';
import {
  sculptColor,
  sculptedBenchesOf,
  sculptLayout,
  sculptMeshBuffer,
} from '../sculptBench';
import { sculptTrestles } from '../sculptTrestles';
import {
  BEAM_DEFAULTS,
  channelSolids,
  coveGeometry,
  coveOf,
  coveStripSolids,
  fibreKitsOf,
  kitArcs,
  kitFibres,
  LIGHT_AMBER,
  lightEmitters,
  projectorMiddle,
  skySolids,
  SPOT_BODY_COLOR,
  spotSolids,
  stripSolids,
} from '../lights';
import type { SecPart } from '../section';
import {
  GLOW,
  lightRigs,
} from './lights3d';
import {
  grilleColor,
  grilleLayout,
  grilleUnder,
} from '../floorGrille';

const GLASS_COLOR = '#bfe2f5';
/** A fibre kit's arc in 3D: a bundle of fibres, this thick (radius, mm). */
const FIBRE_ARC_RADIUS = 5;
/** A room's floor with the lights on (the ground is not drawn then). */
const NIGHT_FLOOR = '#8a8178';

/** Adds a part's mesh — a feature of it when `part` names one (a support's
 *  trestle, a drawn wall's pane), else its own. */
type PartAdd = (m: THREE.Mesh | null, part?: SecPart | null) => void;

/** Adds a room's mesh, tagged with the feature it is when `part` names one;
 *  without one it is the room's own. `ceiling`: it is the room's ceiling (Hide
 *  ceilings, and a clad slab's underside dressed as the ceiling); `ownUV`: its
 *  UVs are true surface mm already; `solid`: the solid it is a piece of. */
type RoomAdd = (m: THREE.Mesh | null, part?: SecPart | null, at?: { ceiling?: boolean; ownUV?: boolean; solid?: Solid }) => void;

/** A group's record: what it holds is all one object's (`replaceObjects`). */
const groupNode = (owner: string, role: NodeRole = 'group'): Parameters<typeof setNode>[1] => ({ owner, role, color: new THREE.Color('#cccccc') });

/** A feature's tag, or null while it has no id (it is then its room's). */
function partOf(kind: SecPart['kind'], id: string | undefined): SecPart | null {
  return id ? { kind, id } : null;
}

/** A bench support's trestle: the support, under bench `bench` (section.ts `supportSrc`). */
function supportPart(id: string, bench?: string): SecPart {
  return { kind: 'support', id, ...(bench ? { bench } : {}) };
}

// Default 3D surface colours per room type — warm timber for saunas, light glossy
// tile for steam rooms. User-set wallColor / benchColor still win.
const SAUNA_WALL_3D = '#c68a4e';
const SAUNA_BENCH_3D = '#d8a862';
const STEAM_WALL_3D = '#dbe3e8';
const STEAM_BENCH_3D = '#c2cbd2';

/** Session cache of underlay textures, keyed by data URL, so live rebuilds reuse
 *  the same GPU texture instead of re-decoding and leaking on every update. */
const textureCache = new Map<string, THREE.Texture>();

const WALL_COLOR = '#d8d2c4';
const DOOR_LEAF_COLOR = '#b98a5a';

/** Normalise a possibly 8-digit (#rrggbbaa) hex to a 6-digit colour three accepts. */
function hex6(c: string | undefined, fallback: string): string {
  if (!c) return fallback;
  const m = /^#?([0-9a-f]{6})/i.exec(c.trim());
  return m ? `#${m[1]}` : fallback;
}

function solidMat(colorHex: string): THREE.Material {
  return withLook(new THREE.MeshStandardMaterial({ color: new THREE.Color(colorHex), roughness: 0.85, metalness: 0.0, side: THREE.DoubleSide }), 'solid', colorHex);
}

/** Timber material with tongue-and-groove grain (sauna walls, ceiling); without
 *  the seams for a bench, whose slats are modelled. Its texture is drawn in its
 *  colour over white: the exports write the colour. */
function woodMat(colorHex: string, seams = true): THREE.Material {
  const map = woodTexture(colorHex, seams);
  return withLook(new THREE.MeshStandardMaterial({ color: new THREE.Color(map ? 0xffffff : colorHex), map: map ?? undefined, roughness: 0.6, metalness: 0.0, side: THREE.DoubleSide }), 'solid', colorHex);
}

/** Glossy ceramic-tile material with a wet clearcoat (steam-room surfaces). */
function tileMat(colorHex: string): THREE.Material {
  const map = tileTexture(colorHex);
  return withLook(new THREE.MeshPhysicalMaterial({ color: new THREE.Color(map ? 0xffffff : colorHex), map: map ?? undefined, roughness: 0.18, metalness: 0.0, clearcoat: 0.7, clearcoatRoughness: 0.25, side: THREE.DoubleSide }), 'solid', colorHex);
}

/** A light fixture: it glows in the normal view, whatever lights the scene —
 *  brighter with the lights on (its role, `glow`). */
function lightMat(): THREE.Material {
  return withLook(new THREE.MeshStandardMaterial({ color: new THREE.Color('#fff4cc'), emissive: new THREE.Color('#ffd27a'), emissiveIntensity: GLOW.off, roughness: 0.5, metalness: 0, side: THREE.DoubleSide }), 'glow', '#fff4cc');
}

/** Brushed-metal material (steel grille, heater body). */
function metalMat(colorHex: string): THREE.Material {
  return withLook(new THREE.MeshStandardMaterial({ color: new THREE.Color(colorHex), roughness: 0.35, metalness: 0.85, side: THREE.DoubleSide }), 'solid', colorHex);
}

/** The wall, ceiling and bench material factories for a room, by its category.
 *  Saunas are all timber (benches keep their per-tier wood tones); steam rooms are
 *  glossy tile, benches included, matching the wall/stone colour so they don't
 *  read as tan wood. A ceiling in the walls' colour IS the wall material. A sauna
 *  clad in a wood, or a steam room in its finishes (`clad`), is built in its
 *  cladding's stand-ins, which `dress` replaces (docs/claude/woods.md); `roof` is
 *  what the ceiling's top and edges are built in — the ceiling's material while
 *  it is not; `frame` a wood glazing's frame: the walls' wood, grain alone (no
 *  painted seams across a jamb) unless the cladding dresses it. */
function roomMats(inst: ProductInstance): {
  wall: THREE.Material; ceil: THREE.Material; roof: THREE.Material; frame: THREE.Material;
  benchOf: (hex: string, structure?: boolean) => THREE.Material; clad: Cladding | null;
} {
  const steam = inst.category === 'steamroom';
  const wallHex = hex6(inst.wallColor, steam ? STEAM_WALL_3D : SAUNA_WALL_3D);
  const stoneHex = hex6(inst.benchColor, STEAM_BENCH_3D);
  const wall = steam ? tileMat(wallHex) : woodMat(wallHex);
  const ceilHex = hex6(ceilingColorOf(inst, wallHex), wallHex);
  // A bench is built slat by slat: one material per colour, not one per slat.
  const benchMats = new Map<string, THREE.Material>();
  const benchOf = (hex: string): THREE.Material => {
    const key = steam ? stoneHex : hex;
    let m = benchMats.get(key);
    if (!m) benchMats.set(key, m = steam ? tileMat(stoneHex) : woodMat(hex, false));
    return m;
  };
  const ceil = ceilHex.toLowerCase() === wallHex.toLowerCase() ? wall : steam ? tileMat(ceilHex) : woodMat(ceilHex);
  const clad = takesWood(inst) ? roomCladding(inst, { wall, ceil }, resolvedOutline(inst, 0), boardAxes(inst))
    : takesFinish(inst) ? finishCladding(inst, { wall, ceil }, resolvedOutline(inst, 0), wallThicknessOf(inst), boardAxes(inst))
    : null;
  if (clad) return { wall: clad.wall, ceil: clad.ceil, roof: clad.roof, frame: clad.wall, benchOf: (hex, structure = false) => clad.bench?.(structure) ?? benchOf(hex), clad };
  return { wall, ceil, roof: ceil, frame: steam ? wall : woodMat(wallHex, false), benchOf, clad: null };
}

/** The way a room's level boards run: along its longest wall, as a shaped
 *  ceiling's do (`CeilingField.boards`). */
function boardAxes(inst: ProductInstance): { u: Vec2; n: Vec2 } {
  return hasCeiling(inst) ? resolveCeiling(inst).boards : { u: { x: 1, y: 0 }, n: { x: 0, y: 1 } };
}

/** Physically-based glass for panels and sauna glass doors (transmissive, so the
 *  environment shows through). */
function glassMat(color?: string): THREE.Material {
  // Clear, sharp glass. Plain alpha blending (no `transmission`) keeps whatever is
  // behind the pane pixel-crisp — transmission samples a separate, blurred buffer
  // and refracts through the slab, which reads as frosted/smeared. A low opacity
  // makes it markedly more see-through; a light clearcoat keeps a glassy sheen.
  return withLook(new THREE.MeshPhysicalMaterial({ color: new THREE.Color(color || GLASS_COLOR), transparent: true, opacity: 0.18, roughness: 0.02, metalness: 0, clearcoat: 0.5, clearcoatRoughness: 0.04, side: THREE.DoubleSide }), 'glass', color || GLASS_COLOR);
}

/**
 * Build the 3D group for a drawing. `getTemplate` resolves a product template by
 * id (for configurable bench heights). Returns a group centred in world space.
 */
export function buildScene(
  drawing: Drawing,
  getTemplate: (templateId: string) => ProductTemplate | undefined,
  hiddenCategories?: Set<string>,
  /** Objects on a hidden layer (and `imported` for a hidden Imported layer). */
  hiddenIds?: ReadonlySet<string>,
  /** Build only these objects (a handle's live rebuild, `Viewer3D.replaceObjects`). */
  only?: ReadonlySet<string>,
): THREE.Group {
  const group = new THREE.Group();
  // Tag every mesh with the instance it belongs to, so the viewer can raycast a
  // click back to a drawing object and select it.
  let currentId = '';
  // A tilted part is built upright, each of its meshes then turned to its tilt.
  let currentTilt: THREE.Matrix4 | null = null;
  const add: PartAdd = (m, part) => {
    if (!m) return;
    if (currentTilt) m.applyMatrix4(currentTilt);
    setNode(m, { owner: currentId, ...lookOf(m), ...(part ? { part } : {}) });
    group.add(m);
  };

  // The parts set into walls (a control unit's display) cut them: a pocket in a
  // drawn wall, a hole through a room's (among its openings: `withWallCuts`).
  const shownParts = drawing.instances.filter((i) => !hiddenIds?.has(i.id) && !hiddenCategories?.has(i.category));
  const cutParts = inWallParts(shownParts);
  // The risers of the parts set into a room's wall (a shower mixer's hot and
  // cold pipes), built with that room: it is built again whenever it or a part
  // set into a wall changes (sceneChanges.ts).
  const risers = partRisers(shownParts);

  for (const placed of drawing.instances) {
    if (only && !only.has(placed.id)) continue;
    if (hiddenCategories?.has(placed.category)) continue; // a discipline toggle hides this on screen
    if (hiddenIds?.has(placed.id)) continue; // so does a hidden layer
    currentId = placed.id;
    const tilted = isTilted(placed);
    currentTilt = tilted ? tiltMatrix4(placed, getTemplate(placed.templateId)) : null;
    const inst: ProductInstance = tilted ? { ...placed, rotationX: undefined, rotationY: undefined } : placed;
    const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
    const template = getTemplate(inst.templateId);

    if (inst.autoBenches) {
      // Its walls with the parts' holes through them, as openings.
      const walled = withWallCuts(inst, cutParts);
      const mats = roomMats(inst);
      const wallMat = mats.wall;
      const benchColor = hex6(inst.benchColor, inst.category === 'steamroom' ? STEAM_BENCH_3D : SAUNA_BENCH_3D);
      const zTop = externalHeight(inst);
      const zCeil = interiorHeight(inst);
      // A ceiling of its own colour sits on the walls instead of sharing their
      // top band, which would leave coplanar faces of two materials fighting.
      // Only the SOLID wall stops there: a pane, an opening and the door leaf
      // keep their own tops, so the colour never moves them.
      const zWall = mats.ceil === wallMat ? zTop : zCeil;
      // The whole room is built at floor level, then lifted by its height-from-
      // ground (like a heater / wall). A sub-group keeps every part in step.
      const base = roomBase(inst);
      const roomGroup = base > 0 ? new THREE.Group() : group;
      const addR: RoomAdd = (m, part, at = {}) => {
        if (!m) return;
        const pieces = mats.clad ? mats.clad.dress(m, { ceiling: !!at.ceiling, ownUV: !!at.ownUV }) : [m];
        // A mesh dressed as several is one solid (a 3D section fills them together).
        const solid = at.solid ?? (pieces.length > 1 || pieces[0] !== m ? {} : undefined);
        for (const piece of pieces) {
          setNode(piece, { owner: currentId, ...lookOf(piece), ...(part ? { part } : {}), ...(solid ? { solid } : {}), ...(at.ceiling ? { ceiling: true } : {}) });
          roomGroup.add(piece);
        }
      };

      for (const rz of risers) if (rz.room === inst.id) add(extrudePrism(riserRing(rz), rz.z0, rz.z1, solidMat(RISER_COLORS[rz.water])));
      // A shaped ceiling: every wall rises to where it meets the underside, and the
      // shell sits on them (docs/claude/ceilings.md, *Shaped ceilings*).
      const cf = isFlatCeiling(inst) ? null : resolveCeiling(inst);
      if (cf) addShapedRoom(walled, cf, W, wallMat, mats.ceil, mats.roof, addR);
      else {
        for (const quad of polyWall(walled, { openings: true }).fill) addR(extrudePrism(quad.map(W), 0, zWall, wallMat));
        // Interior partitions: their own height band and colour, doors cut out.
        for (const iw of inst.innerWalls || []) {
          if (iw.edge == null) continue;
          const mat = partitionMat(iw, wallMat);
          for (const quad of polyInnerWallFill(inst, iw)) {
            if (quad.length >= 3) addR(extrudePrism(quad.map(W), innerWallBase(iw), innerWallTop(inst, iw), mat), partOf('partition', iw.id));
          }
        }
        // Hidden heater walls: false walls floating at their own height band.
        for (const hb of heaterWallBands(inst)) {
          if (hb.poly.length < 3) continue;
          addR(extrudePrism(hb.poly.map(W), heaterWallBase(hb.wall), heaterWallTop(hb.wall), solidMat(hex6(hb.wall.color, WALL_COLOR))), partOf('heaterWall', hb.wall.id));
        }
        // Ceiling on the resolved outline, so it follows any rounded corner.
        const ceil = resolvedOutline(inst, 0);
        addR(ceil.length >= 3 ? extrudePrism(ceil.map(W), zCeil, zTop, mats.ceil) : null, null, { ceiling: true });
      }
      // One solid per slat, the gaps between them open (the plan's own slat
      // quads, the last one the front board), then the frame, trestles and
      // skirting; rounded-corner pieces at the same height band as their tier,
      // and a partition's benches built the same way — each carved round what
      // stands in the room (`carvedSeat`). Each tagged with its bench, so a click
      // in 3D selects it.
      // A corner piece (a room corner's, a rounded end's) is the bench it
      // continues: X-ray (which sees through what has no part) leaves it whole.
      const cornerOf = new Map(polyBenchCornerBands(inst).map((c) => [c.id, c.sourceId]));
      for (const b of roomBenchSolids(inst, { benchTierHeights: inst.benchTierHeights })) {
        const own = hex6(b.color, benchColor);
        const structure = b.part === 'frame' || b.part === 'support';
        const mat = mats.benchOf(structure ? structureColor(own) : own, structure);
        // A support's trestle is the support (under its bench), as in a section.
        const bench = b.part === 'support' && b.support ? supportPart(b.support, b.kind === 'band' ? b.id : undefined)
          : partOf('bench', b.kind === 'band' ? b.id : cornerOf.get(b.id));
        if (b.sweep) { addR(sweptBench(b.sweep.profile, b.sweep.pathAt, W, inst.rotation, mat), bench); continue; }
        addR(extrudePrism(b.poly.map(W), b.base, b.top, mat, b.holes?.map((h) => h.map(W))), bench);
      }
      // Backrests: their boards and spacers, in the bench timber (backrest.ts).
      for (const br of backrestsOf(inst)) {
        const own = hex6(br.color, benchColor);
        for (const piece of backrestSolids(inst, br)) {
          const spacer = piece.part === 'spacer';
          addR(extrudePrism(piece.poly.map(W), piece.base, piece.top, mats.benchOf(spacer ? structureColor(own) : own, spacer)), partOf('fixture', br.id));
        }
      }
      // Sculpted benches: one smooth solid each, in the benches' wood or the
      // inside's finish (else its colour), laid by its own UVs — mm along the
      // bench and round its profile — so the grain follows it (sculptBench.ts).
      for (const sb of sculptedBenchesOf(inst)) {
        const lay = sculptLayout(inst, sb);
        if (lay) addR(meshFromBuf(sculptMeshBuffer(lay), W, inst.rotation, mats.benchOf(hex6(sculptColor(inst, sb), benchColor))), partOf('fixture', sb.id), { ownUV: true });
        // Its trestles, in the structure's timber: the bench (sculptTrestles.ts).
        const tmat = mats.benchOf(structureColor(hex6(sculptColor(inst, sb), benchColor)), true);
        for (const t of sculptTrestles(inst, sb)) {
          for (const b of t.solids) {
            if (b.sweep) addR(sweptBench(b.sweep.profile, b.sweep.pathAt, W, inst.rotation, tmat), partOf('fixture', sb.id));
            else addR(extrudePrism(b.poly.map(W), b.base, b.top, tmat), partOf('fixture', sb.id));
          }
        }
      }
      // The floor grille: its battens, then its top slats on them (floorGrille.ts).
      const grille = grilleLayout(inst);
      if (grille) {
        const own = hex6(grilleColor(inst), benchColor);
        for (const p of [...grille.battens, ...grille.slats]) {
          const batten = p.kind === 'batten';
          const mat = mats.clad?.grille?.(batten) ?? mats.benchOf(batten ? structureColor(own) : own, batten);
          addR(extrudePrism(p.ring.outer.map(W), p.z0, p.z1, mat, p.ring.holes.map((h) => h.map(W))), null);
        }
      }
      // Strip lights, in their places under and behind the benches, and the spots
      // and fibre ends in the ceiling (lights.ts): their light glows.
      // A cove's layer, ledges and valances are the ceiling's; its strip glows.
      const cg = coveGeometry(inst), cove = coveOf(inst);
      if (cg && cove) for (const part of cg.parts) addR(extrudePrism(part.poly.map(W), part.base, part.top, mats.ceil), partOf('fixture', cove.id));
      const strips = [...stripSolids(inst), ...coveStripSolids(inst), ...skySolids(inst)], spots = [...spotSolids(inst), ...channelSolids(inst)];
      if (strips.length || spots.length) {
        const glow = lightMat(), can = metalMat(SPOT_BODY_COLOR);
        for (const piece of strips) addR(extrudePrism(piece.poly.map(W), piece.base, piece.top, 'part' in piece && piece.part === 'can' ? can : glow), partOf('fixture', piece.id));
        for (const piece of spots) addR(extrudePrism(piece.poly.map(W), piece.base, piece.top, piece.part === 'can' ? can : glow), partOf('fixture', piece.id));
      }
      // A fibre kit's arcs: from its projector over the ceiling to its fibres, thin tubes.
      const kits = fibreKitsOf(inst);
      if (kits.length) {
        const cable = withLook(new THREE.MeshStandardMaterial({ color: new THREE.Color(LIGHT_AMBER), roughness: 0.6, metalness: 0 }), 'solid', LIGHT_AMBER);
        for (const kit of kits) {
          const part = kit.projectorId ? drawing.instances.find((i) => i.id === kit.projectorId) : undefined;
          if (!part) continue;
          for (const arc of kitArcs(inst, projectorMiddle(inst, part), kitFibres(inst, kit))) {
            const curve = new THREE.CatmullRomCurve3(arc.map(([x, y, z]) => { const w = W({ x, y }); return new THREE.Vector3(w.x, z, -w.y); }));
            addR(new THREE.Mesh(new THREE.TubeGeometry(curve, arc.length * 2, FIBRE_ARC_RADIUS, 5, false), cable), partOf('fixture', kit.id));
          }
        }
      }
      // Its lights switched on: the view's rig (Lights on) and the file's (a GLB's),
      // hidden until the viewer shows one (three/lights3d.ts). The view's has a
      // floor for them to fall on: with the lights on the ground goes.
      const emitters = lightEmitters(inst, drawing.lightBeams ?? BEAM_DEFAULTS);
      if (emitters.some((e) => e.kind !== 'fibre')) {
        const rigs = lightRigs(emitters, W, inst.rotation);
        const floor = extrudePrism(polyInnerCarved(inst).map(W), 0, FLOOR_Z.nightFloor, solidMat(NIGHT_FLOOR));
        if (floor) rigs.view.add(floor);
        for (const [rig, role] of [[rigs.view, 'viewRig'], [rigs.file, 'fileRig']] as const) {
          const id = currentId;
          rig.traverse((o) => setNode(o, o === rig ? groupNode(id, role) : (o as THREE.Mesh).isMesh ? { owner: id, ...lookOf(o) } : groupNode(id, 'light')));
          roomGroup.add(rig);
        }
      }
      addFeatures(walled, cf, W, zWall, wallMat, mats.frame, addR);
      if (base > 0) { roomGroup.position.y = base; setNode(roomGroup, groupNode(currentId)); group.add(roomGroup); }
      continue;
    }

    // A part: what stands round its body, then its body (partLook.ts), from its
    // height from ground (`partSpan`) — a heater or a shower drawn from its
    // drawing or image floats as its box.
    const look = partLook(placed);
    const span = partSpan(inst, template ?? null);
    for (const a of look.attachments) addAttachment(a, span, W, add);
    // A projected body is its model (a stored one, or a built body's generated
    // from its figures: builtModels.ts) — the one the sections cut.
    const body = look.body;
    if (isProjected(body)) addModel(inst, bodyModel(inst, body), W, span, add);
    else {
      switch (body.kind) {
        case 'run': addNodeObject(inst, lifted(add, span[0]), grilleUnder(drawing.instances, inst)); break;
        case 'heaterBlock': addHeaterBody(inst, W, span, add); break;
        case 'box': addBox(inst, span, add); break;
        default: unreachable(body, 'part body');
      }
    }
  }
  currentTilt = null;

  for (const wall of drawing.walls || []) {
    if (only && !only.has(wall.id)) continue;
    if (hiddenIds?.has(wall.id)) continue;
    currentId = wall.id;
    // Points are world-space. The wall is ONE mesh of flat faces (`wallMeshFaces`):
    // its side faces run unbroken over a lintel or a sill, so no seam shows there.
    const base = wallBase(wall), top = base + wallHeight(wall);
    add(facesMesh(wallMeshFaces(wall, wallPockets(wall, cutParts)), solidMat(hex6(wall.fill, WALL_COLOR))));
    for (const sp of wallFeatureSpans(wall)) {
      // A pane and a door leaf are their feature's, as a room's are.
      const part = partOf('wallFeature', sp.f.id);
      const addF = (m: THREE.Mesh | null): void => add(m, part);
      for (const l of wallFeatureLayers(wall, sp.f)) if (l.glass) addF(extrudePrism(wallPaneQuad(sp), l.z0, l.z1, glassMat(sp.f.color)));
      if (sp.f.kind === 'door') {
        // The leaf, open as in the plan, flush against its hinge jamb: centred on
        // the hinge line, half of it would run into the wall.
        const sw = wallDoorSwing(sp);
        const k = 20 / (Math.hypot(sw.closed.x - sw.hinge.x, sw.closed.y - sw.hinge.y) || 1);
        const sh = { x: (sw.closed.x - sw.hinge.x) * k, y: (sw.closed.y - sw.hinge.y) * k };
        const at = (q: Vec2): Vec2 => ({ x: q.x + sh.x, y: q.y + sh.y });
        addF(extrudePrism(thinPanel(at(sw.hinge), at(sw.open), 40), base, Math.min(top, wallFeatureTop(wall, sp.f)), solidMat(DOOR_LEAF_COLOR)));
      }
    }
  }

  // User-drawn polygons/ellipses extrude only when given a 3D height (their new
  // height / height-from-ground parameters, like a heater); height 0 stays flat.
  for (const poly of drawing.polygons || []) {
    if (only && !only.has(poly.id)) continue;
    if (hiddenIds?.has(poly.id)) continue;
    const h = polygonHeight(poly);
    if (h <= 0 || poly.points.length < 3) continue;
    currentId = poly.id;
    const base = polygonBase(poly);
    add(extrudePrism(poly.points, base, base + h, solidMat(hex6(poly.fill, PRODUCT_COLOR))));
  }

  // Physical connections: each a tube along its pipe's route, elbows and all
  // (docs/claude/connections.md *Pipes*); a hidden discipline hides them.
  const tubes = pipeTubes(drawing, (c) => !(only && !only.has(c.id)) && !hiddenIds?.has(c.id) && !hiddenCategories?.has(connDiscipline(c)));
  for (const t of tubes) {
    currentId = t.id;
    add(tubeMesh(t.pts, t.r, t.ri, solidMat(t.color)));
  }
  // Schematic connections: up from each end to under the nearest wall's top,
  // along the plan route, down to the other end — while their discipline shows.
  const keepConn = (c: Connection): boolean => !(only && !only.has(c.id)) && !hiddenIds?.has(c.id) && !hiddenCategories?.has(connDiscipline(c));
  // Each a 1 px line (WebGL draws every line 1 px wide), dashed as on the plan.
  for (const l of connLines3d(drawing, keepConn)) {
    if (!l.segs.length) continue;
    const pos = new Float32Array(l.segs.length * 6);
    l.segs.forEach(([a, b], i) => pos.set([a.x, a.z, -a.y, b.x, b.z, -b.y], i * 6));
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const color = hex6(l.color, '#888888');
    const line = new THREE.LineSegments(geo, withLook(new THREE.LineBasicMaterial({ color }), 'line', color));
    setNode(line, { owner: l.id, ...lookOf(line) });
    group.add(line);
  }

  if (!only && !hiddenIds?.has('imported')) addBackground(drawing, group);
  // Solid surfaces cast and receive shadows; glass only receives, so it doesn't
  // paint an opaque silhouette on the floor (`ROLE`).
  group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = ROLE[nodeOf(mesh)?.role ?? 'solid'].shadow;
    mesh.receiveShadow = true;
  });
  return group;
}

/** Imported plan underlays (PDF/image) as textured planes on the floor. Each is
 *  drawn over what lies flush with it — the floor a room gets with the lights
 *  on, 1 mm thick — never fighting it for the pixels: the underlay shows. */
function addBackground(drawing: Drawing, group: THREE.Group): void {
  const imgs = drawing.backgroundImages ?? (drawing.backgroundImage ? [drawing.backgroundImage] : []);
  for (const bi of imgs) {
    if (!bi.dataUrl) continue;
    let tex = textureCache.get(bi.dataUrl);
    if (!tex) {
      tex = new THREE.TextureLoader().load(bi.dataUrl);
      tex.colorSpace = THREE.SRGBColorSpace;
      textureCache.set(bi.dataUrl, tex);
    }
    const geo = new THREE.PlaneGeometry(bi.widthMm, bi.heightMm);
    geo.rotateX(-Math.PI / 2); // lie flat; plan +Y maps to world -Z
    const mat = withLook(new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: bi.opacity ?? 1, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4 }), 'underlay', '#ffffff');
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(bi.x, FLOOR_Z.underlay, -bi.y);
    setNode(mesh, { owner: null, ...lookOf(mesh) });
    mesh.rotation.y = -((bi.rotation ?? 0) * Math.PI) / 180;
    group.add(mesh);
  }
}

/** A shaped room's walls, partitions and heater walls, each rising to where it
 *  meets the ceiling's underside (a partition with no height of its own follows
 *  it, anything taller is cut by it), and the shell — none for a 0 mm ceiling. */
function addShapedRoom(
  inst: ProductInstance, cf: CeilingField, W: (p: Vec2) => Vec2, wallMat: THREE.Material, ceilMat: THREE.Material, roofMat: THREE.Material,
  add: RoomAdd,
): void {
  const prism = (ring: Vec2[], z0: number, top: (p: Vec2) => number, mat: THREE.Material, part?: SecPart | null): void => {
    if (ring.length >= 3) add(meshFromBuf(prismUnder(ring, z0, top, cf), W, inst.rotation, mat), part);
  };
  for (const quad of polyWall(inst, { openings: true }).fill) prism(quad, 0, (p) => wallTopAt(cf, p), wallMat);
  for (const iw of inst.innerWalls || []) {
    if (iw.edge == null) continue;
    const mat = partitionMat(iw, wallMat);
    for (const quad of polyInnerWallFill(inst, iw)) prism(quad, innerWallBase(iw), (p) => innerWallTopAt(iw, underAt(cf, p)), mat, partOf('partition', iw.id));
  }
  for (const hb of heaterWallBands(inst)) {
    const z1 = heaterWallTop(hb.wall);
    prism(hb.poly, heaterWallBase(hb.wall), (p) => Math.min(z1, underAt(cf, p)), solidMat(hex6(hb.wall.color, WALL_COLOR)), partOf('heaterWall', hb.wall.id));
  }
  // One solid, closed by its seat over the walls' tops: a 3D section fills it.
  const parts = shellParts(cf), { outer, under, fascia } = parts;
  const solid: Solid = { seat: meshFromBuf(shellSeat(cf, parts), W, inst.rotation, ceilMat)?.geometry };
  if (roofMat === ceilMat) {
    const whole = { pos: [...outer.pos, ...under.pos, ...fascia.pos], nrm: [...outer.nrm, ...under.nrm, ...fascia.nrm], uv: [...outer.uv, ...under.uv, ...fascia.uv] };
    add(meshFromBuf(whole, W, inst.rotation, ceilMat), null, { ceiling: true, solid });
    return;
  }
  // A clad room's: the underside in the ceiling's wood, the roof the outside's.
  const roof = { pos: [...outer.pos, ...fascia.pos], nrm: [...outer.nrm, ...fascia.nrm], uv: [...outer.uv, ...fascia.uv] };
  for (const [buf, mat] of [[under, ceilMat], [roof, roofMat]] as const) {
    add(meshFromBuf(buf, W, inst.rotation, mat), null, { ceiling: true, ownUV: true, solid });
  }
}

/** A room's panes, openings and doors: the pieces they make of its walls
 *  (roomFeaturePieces.ts), each built — under a shaped ceiling up to where it
 *  meets the underside, a lintel or a header capped at the wall's top even where
 *  the ceiling leaves it no height, so the shell has no hole over it. `zWall`:
 *  where a flat room's solid wall stops. */
function addFeatures(inst: ProductInstance, cf: CeilingField | null, W: (p: Vec2) => Vec2, zWall: number, wallMat: THREE.Material, frameMat: THREE.Material, add: RoomAdd): void {
  const glass = new Map<string, THREE.Material>(), partitions = new Map<InnerWall, THREE.Material>();
  const matOf = (s: PieceStuff): THREE.Material => {
    switch (s.kind) {
      case 'wall': return wallMat;
      case 'glass': { const k = s.color ?? ''; let m = glass.get(k); if (!m) glass.set(k, m = glassMat(s.color)); return m; }
      case 'partition': { let m = partitions.get(s.iw); if (!m) partitions.set(s.iw, m = partitionMat(s.iw, wallMat)); return m; }
      default: return unreachable(s, 'piece stuff');
    }
  };
  for (const p of roomFeaturePieces(inst, { wallTop: zWall })) {
    if (!BUILT[p.role]) continue;
    const mat = matOf(p.stuff);
    if (!cf || !p.underCeiling) { add(extrudePrism(p.ring.map(W), p.z0, p.z1, mat), p.part); continue; }
    const span = pieceSpanAt(p, cf);
    add(meshFromBuf(prismUnder(p.ring, span.bottom, span.top, cf, { capWhenCollapsed: p.capped }), W, inst.rotation, mat), p.part);
  }
  for (const { mesh, part } of glazingMeshes(inst, W, frameMat)) add(mesh, part);
}

/** A partition's material: its own colour, else the room's walls'. */
function partitionMat(iw: InnerWall, wallMat: THREE.Material): THREE.Material {
  return iw.color ? solidMat(hex6(iw.color, WALL_COLOR)) : wallMat;
}

/** A drawn wall's holes as pick volumes — its openings (sill to architrave) and
 *  its doorways (its base to the door's top), each within the wall's own height —:
 *  with the wall selected, a click through one picks it, as a room's do. */
export function wallFeatureVolumes(wall: Wall): PickVolume[] {
  const b = wallBase(wall), T = b + wallHeight(wall);
  const out: PickVolume[] = [];
  for (const sp of wallFeatureSpans(wall)) {
    if (sp.f.kind === 'glass') continue; // its pane is a mesh
    const z0 = Math.max(b, wallFeatureBase(wall, sp.f)), z1 = Math.min(T, wallFeatureTop(wall, sp.f));
    const ring = wallFeatureQuad(sp);
    if (ring.length >= 3 && z1 > z0 + 1e-6) out.push({ src: { id: wall.id, part: { kind: 'wallFeature', id: sp.f.id } }, ring, z0, z1 });
  }
  return out;
}

/** `add`, each mesh it adds raised `dz` — a part built from the floor set on its
 *  height from ground. */
function lifted(add: PartAdd, dz: number): PartAdd {
  return dz ? (m, part) => { if (m) m.position.y += dz; add(m, part); } : add;
}

/** The turn a tilted part's upright meshes take (three's frame: x, y up, z =
 *  −plan y): about its upright body's middle, by its tilt along its plan turn
 *  (Rz·T·Rzᵀ), to its tilted body's middle — its lowest point at its base. */
function tiltMatrix4(inst: ProductInstance, template: ProductTemplate | undefined): THREE.Matrix4 {
  const [base, top] = partSpan(inst, template ?? null), h = partHeight(inst, template ?? null);
  const a = mulMat3(partMatrix(inst), rotZ(-inst.rotation));
  const m3 = [[a[0], a[2], -a[1]], [a[6], a[8], -a[7]], [-a[3], -a[5], a[4]]];
  const c0 = [inst.x, base + h / 2, -inst.y], c1 = [inst.x, (base + top) / 2, -inst.y];
  const t = [0, 1, 2].map((r) => c1[r] - (m3[r][0] * c0[0] + m3[r][1] * c0[1] + m3[r][2] * c0[2]));
  return new THREE.Matrix4().set(m3[0][0], m3[0][1], m3[0][2], t[0], m3[1][0], m3[1][1], m3[1][2], t[1], m3[2][0], m3[2][1], m3[2][2], t[2], 0, 0, 0, 1);
}

/** What stands round a part's body (partLook.ts): its guard cover's bands from
 *  the floor to the body's top (`span`'s); its clearance gap is air. */
function addAttachment(a: PartAttachment, span: [number, number], W: (p: Vec2) => Vec2, add: (m: THREE.Mesh | null) => void): void {
  switch (a.kind) {
    case 'cover': {
      const mat = solidMat(hex6(a.color, HEATER_COVER_COLOR));
      for (const b of a.bands) add(extrudePrism(boxRing(b, W), 0, span[1], mat));
      return;
    }
    case 'gap': return;
    default: unreachable(a, 'attachment');
  }
}

/** A part's plain box: its footprint extruded over its span. */
function addBox(inst: ProductInstance, [base, top]: [number, number], add: (m: THREE.Mesh | null) => void): void {
  add(extrudePrism(instanceCorners(inst), base, top, solidMat(hex6(inst.color, PRODUCT_COLOR))));
}

/** A heater's or a shower's body box (a heater block), in its colour as metal. */
function addHeaterBody(inst: ProductInstance, W: (p: Vec2) => Vec2, [base, top]: [number, number], add: (m: THREE.Mesh | null) => void): void {
  const hw = inst.width / 2, hd = inst.depth / 2;
  add(extrudePrism(boxRing({ x0: -hw, y0: -hd, x1: hw, y1: hd }, W), base, top, metalMat(hex6(inst.color, HEATER_BODY_COLOR))));
}

/** A part's model (`PartModel3d`: its stored one, or its built body's —
 *  builtModels.ts), from its Height from ground, through the one builder
 *  (shared/partSolids.ts): each colour role one material (`body` the copy's own
 *  colour; the model's water, glass, metal and acrylic roles as such), its boxes
 *  and prisms extruded with their holes, its hulls, its cylinders and revolved
 *  bodies revolved, its pipes tubes along their centrelines (a connection's
 *  tube, pipes3d.ts: solid, closed at its ends). */
function addModel(inst: ProductInstance, m: PartModel3d, W: (p: Vec2) => Vec2, [base]: [number, number], add: (m: THREE.Mesh | null) => void): void {
  const turned = (q: Vec2): Vec2 => W({ x: q.y, y: -q.x });
  for (const g of solidGroups(m, inst.color, PRODUCT_COLOR)) {
    const hex = hex6(g.colour, PRODUCT_COLOR);
    const mat = g.water ? waterMat(hex) : g.glass ? glassMat(hex) : g.metal ? metalMat(hex) : g.acrylic ? acrylicMat(hex) : solidMat(hex);
    for (const s of g.solids) {
      const mesh = solidMesh(s);
      if ('prism' in mesh) add(extrudePrism(mesh.prism.ring.map(W), base + mesh.prism.z0, base + mesh.prism.z1, mat, mesh.prism.holes?.map((h) => h.map(W))));
      else if ('hull' in mesh) add(hullMesh(mesh.hull.map((q) => to3(W({ x: q.x, y: q.y }), base + q.z)), mat));
      else if ('tube' in mesh) {
        const o = W({ x: 0, y: 0 }), dir = (q: { x: number; y: number; z: number }) => { const w = W(q); return { x: w.x - o.x, y: w.y - o.y, z: q.z }; };
        add(tubeMesh(mesh.tube.pts.map((q) => ({ ...W(q), z: base + q.z })), mesh.tube.r, 0, mat, [dir(mesh.tube.ends[0]), dir(mesh.tube.ends[1])]));
      }
      else add(revolvedMesh(mesh.revolve.stations, mesh.revolve.axis, base, mesh.revolve.turned ? turned : W, mat));
    }
  }
}

/** A surface of revolution about a local x axis at `axis` (across, and up from
 *  `base`): consecutive `[x, radius]` stations joined by cones, or, at the same
 *  x, by a flat ring. Smooth normals round it. */
function revolvedMesh(stations: ReadonlyArray<readonly [number, number]>, axis: { y: number; z: number }, base: number, W: (p: Vec2) => Vec2, mat: THREE.Material, segs = 40): THREE.Mesh | null {
  const o = W({ x: 0, y: 0 }), ex = W({ x: 1, y: 0 }), ey = W({ x: 0, y: 1 });
  const ux = { x: ex.x - o.x, y: ex.y - o.y }, uy = { x: ey.x - o.x, y: ey.y - o.y };
  const pos: number[] = [], nor: number[] = [];
  // A local point (x along, then round the axis by `a` at radius r) in three's frame.
  const put = (x: number, r: number, a: number, n: readonly [number, number, number]): void => {
    const w = W({ x, y: axis.y + r * Math.cos(a) });
    pos.push(w.x, base + axis.z + r * Math.sin(a), -w.y);
    const px = n[0] * ux.x + n[1] * uy.x, py = n[0] * ux.y + n[1] * uy.y;
    nor.push(px, n[2], -py);
  };
  for (let i = 0; i + 1 < stations.length; i++) {
    const [x0, r0] = stations[i], [x1, r1] = stations[i + 1];
    if (Math.abs(x1 - x0) < 1e-9 && Math.abs(r1 - r0) < 1e-9) continue;
    const flat = Math.abs(x1 - x0) < 1e-9, slope = flat ? 0 : (r1 - r0) / (x1 - x0), k = 1 / Math.hypot(1, slope);
    // Wound so its normal points out: a flat ring narrowing toward +x faces +x.
    const ringN = r1 < r0 ? 1 : -1;
    for (let j = 0; j < segs; j++) {
      const a0 = (2 * Math.PI * j) / segs, a1 = (2 * Math.PI * (j + 1)) / segs;
      const n = (a: number): [number, number, number] => (flat ? [ringN, 0, 0] : [-slope * k, Math.cos(a) * k, Math.sin(a) * k]);
      put(x0, r0, a0, n(a0)); put(x1, r1, a1, n(a1)); put(x1, r1, a0, n(a0));
      put(x0, r0, a0, n(a0)); put(x0, r0, a1, n(a1)); put(x1, r1, a1, n(a1));
    }
  }
  if (!pos.length) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  return new THREE.Mesh(geo, mat);
}

/** Glossy sanitary acrylic: a hot tub's shell, its seats and the deck cap, an
 *  essence doser's board or shelf. */
function acrylicMat(colorHex: string): THREE.Material {
  return withLook(new THREE.MeshPhysicalMaterial({ color: new THREE.Color(colorHex), roughness: 0.22, metalness: 0.0, clearcoat: 0.6, clearcoatRoughness: 0.2, side: THREE.DoubleSide }), 'solid', colorHex);
}

/** Water: tinted, and clear enough to show the seats and footwell through it.
 *  FrontSide, so looked down on only its SURFACE blends — with DoubleSide the
 *  floor of the water body would blend a second time and every seat would read
 *  as sitting under two layers of water. It writes no depth, so it can never hide
 *  the shell it fills; the opaque shell is drawn first and depth-tests it. */
function waterMat(colorHex: string): THREE.Material {
  return withLook(new THREE.MeshPhysicalMaterial({ color: new THREE.Color(colorHex), transparent: true, opacity: 0.5, roughness: 0.04, metalness: 0.0, clearcoat: 1.0, clearcoatRoughness: 0.03, depthWrite: false, side: THREE.FrontSide }), 'water', colorHex);
}

/** A node object (salt wall / heated bench): a filled band per segment (salt uses
 *  its frame footprint; benches offset the node line by the bench depth), extruded
 *  to the object's height. Node coordinates are already world-space. */
function addNodeObject(inst: ProductInstance, add: PartAdd, floor = 0): void {
  const nodes = inst.nodes!;
  const salt = inst.category === 'saltwall';
  const zTop = objectHeight(inst);
  const mat = solidMat(hex6(inst.color, PRODUCT_COLOR));
  const slats = nodeBenchSolids(inst, floor);
  if (slats.length) {
    const frame = solidMat(structureColor(hex6(inst.color, PRODUCT_COLOR)));
    for (const s of slats) {
      const m = s.part === 'frame' || s.part === 'support' ? frame : mat;
      const mesh = s.sweep ? sweptBench(s.sweep.profile, s.sweep.pathAt, (p) => p, 0, m) : extrudePrism(s.poly, s.base, s.top, m);
      // A support's trestle is the support, as in a section.
      add(mesh, s.part === 'support' && s.support ? supportPart(s.support) : null);
    }
    return;
  }
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i], b = nodes[i + 1];
    let quad: Vec2[];
    if (salt) {
      quad = saltWallHull(a, b);
    } else {
      const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1;
      const nx = dy / L, ny = -dx / L, d = inst.depth; // body-side normal (matches the renderer)
      quad = [a, b, { x: b.x + nx * d, y: b.y + ny * d }, { x: a.x + nx * d, y: a.y + ny * d }];
    }
    add(extrudePrism(quad, 0, zTop, mat));
  }
}


/** A steam bench: its profile swept along the bench (`pathAt`, room-local), one
 *  closed mesh standing on the room's floor. */
function sweptBench(profile: BenchProfilePoint[], pathAt: (x: number) => Vec2[], W: (p: Vec2) => Vec2, rotation: number, mat: THREE.Material): THREE.Mesh | null {
  const ring = profileRing(profile);
  return meshFromBuf(sweepMesh(profileSweepFaces(ring, pathAt), ring), W, rotation, mat);
}
